import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import FilterPanel from '../components/FilterPanel';
import { ProductCard, ProductCardSkeleton, Loader } from '../components/design-system';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import api from '../utils/api';
import { cn } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { trackSearch } from '../utils/fbPixel';
import { productPath } from '../utils/helpers';

const Products = () => {
  const { addItem } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableFilters, setAvailableFilters] = useState(null);
  const [relevanceMode, setRelevanceMode] = useState(false);
  const [totalFiltersApplied, setTotalFiltersApplied] = useState(0);
  
  // Initialize filters from URL params
  const getInitialFilters = () => ({
    brand: searchParams.get('brand') || '',
    perfume_type: searchParams.get('perfume_type') || '',
    gender: searchParams.get('gender') || '',
    olfactory_family: searchParams.get('olfactory_family') || '',
    mood: searchParams.get('mood') || '',
    popularity: searchParams.get('popularity') || '',
    intensity: searchParams.get('intensity') || ''
  });
  
  const [selectedFilters, setSelectedFilters] = useState(getInitialFilters);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('match');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Count active filters
  const countActiveFilters = () => {
    let count = 0;
    if (selectedFilters.gender) count++;
    if (selectedFilters.perfume_type) count++;
    if (selectedFilters.intensity) count++;
    if (selectedFilters.olfactory_family) count++;
    if (selectedFilters.mood) count++;
    if (selectedFilters.popularity) count++;
    return count;
  };

  // Update filters when URL changes
  useEffect(() => {
    const filtersFromUrl = {
      brand: searchParams.get('brand') || '',
      perfume_type: searchParams.get('perfume_type') || '',
      gender: searchParams.get('gender') || '',
      olfactory_family: searchParams.get('olfactory_family') || '',
      mood: searchParams.get('mood') || '',
      popularity: searchParams.get('popularity') || '',
      intensity: searchParams.get('intensity') || ''
    };
    setSelectedFilters(filtersFromUrl);
    // Initialize search query from URL
    setSearchQuery(searchParams.get('search') || '');
    setPage(1);
  }, [searchParams]);

  // Load available filters
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await api.get('/products/filters');
        setAvailableFilters(response.data);
      } catch (error) {
        console.error('Error loading filters:', error);
      }
    };
    loadFilters();
  }, []);

  // Load products
  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const activeFilterCount = countActiveFilters();
        const params = {
          page,
          limit: 12,
          sort_by: sortBy,
          sort_order: 'desc',
          // Use relevance mode when 2+ filters are active
          use_relevance: activeFilterCount >= 2
        };

        if (searchQuery) params.search = searchQuery;
        if (selectedFilters.brand) params.brand = selectedFilters.brand;
        if (selectedFilters.perfume_type) params.perfume_type = selectedFilters.perfume_type;
        if (selectedFilters.gender) params.gender = selectedFilters.gender;
        if (selectedFilters.olfactory_family) params.olfactory_family = selectedFilters.olfactory_family;
        if (selectedFilters.mood) params.mood = selectedFilters.mood;
        if (selectedFilters.popularity) params.popularity = selectedFilters.popularity;
        if (selectedFilters.intensity) params.intensity = selectedFilters.intensity;

        const response = await api.get('/products', { params });
        setProducts(response.data.products);
        setPagination(response.data.pagination);
        setRelevanceMode(response.data.relevance_mode || false);
        setTotalFiltersApplied(response.data.total_filters || activeFilterCount);
        
        // Track Search for Facebook Pixel (only when there's a search query)
        if (searchQuery && searchQuery.trim()) {
          trackSearch(searchQuery, response.data.products || []);
        }
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [searchQuery, selectedFilters, sortBy, page]);

  // Reflects filters + search query into the URL (replace, not push)
  const syncUrl = (filters, search) => {
    const params = {};
    if (search) params.search = search;
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    setSearchParams(params, { replace: true });
  };

  const handleFilterChange = (key, value) => {
    let newFilters;
    if (key === 'clear') {
      newFilters = {
        brand: '',
        perfume_type: '',
        gender: '',
        olfactory_family: '',
        mood: '',
        popularity: '',
        intensity: ''
      };
    } else {
      newFilters = { ...selectedFilters, [key]: value };
    }
    setSelectedFilters(newFilters);
    syncUrl(newFilters, searchQuery);
    setPage(1); // Reset to first page
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    syncUrl(selectedFilters, query);
    setPage(1);
  };

  return (
    <div className="bg-brand-bone min-h-screen" data-testid="products-page">
      {/* Search Section - Compact & Creative */}
      <section className="bg-white border-b border-brand-black/10">
        <div className="luxury-container py-4">
          <div className="relative max-w-2xl mx-auto">
            {/* Decorative elements */}
            <div className="absolute -left-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-gradient-to-r from-brand-cobalt/20 to-transparent rounded-full blur-xl hidden sm:block" />
            <div className="absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-gradient-to-l from-brand-magenta/20 to-transparent rounded-full blur-xl hidden sm:block" />
            
            {/* Search Bar with subtle glow */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-brand-cobalt/10 via-transparent to-brand-magenta/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
              <SearchBar onSearch={handleSearch} initialQuery={searchQuery} />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-4">
        <div className="luxury-container">
          <div className="flex flex-col lg:flex-row lg:gap-8">
            {/* Desktop Filters */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24">
                {availableFilters && (
                  <FilterPanel
                    filters={availableFilters}
                    selectedFilters={selectedFilters}
                    onFilterChange={handleFilterChange}
                    availableFilters={availableFilters}
                  />
                )}
              </div>
            </aside>

            {/* Products Grid */}
            <div className="flex-1 min-w-0">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-2 mb-6 pb-4 border-b border-brand-cobalt/20">
                <div className="flex items-center gap-2 sm:gap-4">
                  {/* Mobile Filter Button */}
                  <button
                    onClick={() => setShowMobileFilters(true)}
                    className="lg:hidden flex items-center gap-2 px-3 py-2 border border-brand-black/20 hover:border-brand-cobalt transition-colors text-sm text-brand-black"
                  >
                    <SlidersHorizontal size={16} />
                    <span className="text-label-md">Filtros</span>
                  </button>

                  {pagination && (
                    <p className="text-xs sm:text-body-sm text-brand-black/60">
                      {pagination.total} productos encontrados
                    </p>
                  )}
                </div>

                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-2 sm:px-4 py-2 border border-brand-black/15 bg-brand-bone text-brand-black text-xs sm:text-body-sm focus:outline-none focus:border-brand-cobalt transition-colors max-w-[160px] sm:max-w-none"
                >
                  <option value="match">Mayor coincidencia</option>
                  <option value="created_at">Más recientes</option>
                  <option value="sales_count">Más vendidos</option>
                  <option value="rating_average">Mejor valorados</option>
                  <option value="price">Precio</option>
                </select>
              </div>

              {/* Products Grid */}
              {loading ? (
                <div className="luxury-grid-4">
                  {[...Array(12)].map((_, i) => (
                    <ProductCardSkeleton key={i} />
                  ))}
                </div>
              ) : products.length > 0 ? (
                <>
                  <div className="luxury-grid-4">
                    {products.map((product) => (
                      <ProductCard
                        key={product.id}
                        image={product.main_image}
                        productCode={product.product_code}
                        name={product.inspired_by_name}
                        brand={product.inspired_by_brand}
                        price={product.price_40ml || 250}
                        gender={product.gender}
                        intensity={product.intensity}
                        stock={product.stock > 0 || product.stock_40ml > 0 ? 1 : 0}
                        matchPercentage={product.match_percentage}
                        matchedCriteria={product.matched_criteria}
                        badge={
                          product.popularity?.includes('Nuevo Lanzamiento')
                            ? { variant: 'new', label: 'Nuevo' }
                            : product.popularity?.includes('Más vendidos')
                            ? { variant: 'bestseller', label: 'Más vendido' }
                            : null
                        }
                        href={productPath(product)}
                        onAddToCart={() => (product.stock > 0 || product.stock_40ml > 0) && addItem(product)}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination && pagination.pages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-12">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 border border-brand-black/20 text-brand-black hover:border-brand-cobalt disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Anterior
                      </button>
                      {[...Array(pagination.pages)].map((_, i) => (
                        <button
                          key={i + 1}
                          onClick={() => setPage(i + 1)}
                          className={cn(
                            "w-10 h-10 border transition-colors",
                            page === i + 1
                              ? "bg-brand-cobalt text-white border-brand-cobalt"
                              : "border-brand-black/20 text-brand-black hover:border-brand-cobalt"
                          )}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                        disabled={page === pagination.pages}
                        className="px-4 py-2 border border-brand-black/20 text-brand-black hover:border-brand-cobalt disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Siguiente
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-20">
                  <p className="text-body-lg text-brand-black/60 mb-4">
                    No se encontraron productos
                  </p>
                  <button
                    onClick={() => handleFilterChange('clear', null)}
                    className="text-brand-cobalt hover:underline"
                  >
                    Limpiar filtros
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Filters Modal */}
      {showMobileFilters && (
        <div className="fixed inset-0 bg-black/80 z-50 lg:hidden">
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-brand-bone border-l border-brand-cobalt/20 overflow-y-auto animate-slide-in-right">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-heading-lg text-brand-black">Filtros</h2>
                <button onClick={() => setShowMobileFilters(false)} className="text-brand-black/50 hover:text-brand-black">
                  <X size={24} />
                </button>
              </div>
              {availableFilters && (
                <FilterPanel
                  filters={availableFilters}
                  selectedFilters={selectedFilters}
                  onFilterChange={handleFilterChange}
                  availableFilters={availableFilters}
                />
              )}
              <button
                onClick={() => setShowMobileFilters(false)}
                className="w-full mt-6 brand-btn text-label-md"
              >
                Ver Resultados
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
